import { TFileUploadHandle } from '@qr-front/ui/components/FileUpload'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { FileUploadToMediaStorage } from 'components/FileUploadToMediaStorage'
import { GENERATOR_FORM_ANIMATION_DURATION } from 'features/generator/components/GeneratorFormSection'
import { useAtom, useSetAtom } from 'jotai'
import { RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, RefObject, useImperativeHandle, useRef } from 'react'
import { TUploadedMedia } from 'services/mediaStorage'

import { getStore } from '@/store'

import { TContentFormHandle } from '../qrContentFormTypes'
import { pdfAtom, pdfFileAtom } from './PdfFile'

const MAX_FILE_SIZE = 20 * 1024 * 1024

export const ACCEPTED_MIME_TYPES = [
	'application/pdf',
	'application/octet-stream',
	'application/x-pdf',
]

export const resetPdf = () => {
	getStore().set(pdfAtom, RESET)
	getStore().set(pdfFileAtom, RESET)
}

const validatePdf = ({
	fileUploadRef,
	uploadErrorMessage,
	containerRef,
}: {
	fileUploadRef: RefObject<TFileUploadHandle | null>
	uploadErrorMessage: string
	containerRef: RefObject<HTMLDivElement | null>
}) => {
	const pdfFile = getStore().get(pdfFileAtom)
	const isValid = Boolean(pdfFile)
	if (!isValid) {
		fileUploadRef.current?.setError?.(uploadErrorMessage)
		setTimeout(() => {
			containerRef.current?.scrollIntoView?.({
				behavior: 'smooth',
				block: 'center',
			})
		}, GENERATOR_FORM_ANIMATION_DURATION * 1.25)
	}
	return isValid
}

export const PdfUpload = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const fileUploadRef = useRef<TFileUploadHandle>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	const setPdf = useSetAtom(pdfAtom)
	const [file, setFile] = useAtom(pdfFileAtom)

	const uploadErrorMessage = t(
		'ui__field_validation_errors__generic__required',
	)

	const fileUploadTranslations = {
		title: t('generator__content_form_section__pdf__file_description'),
		caption: t('generator__content_form_section__pdf__max_file_size', {
			size: MAX_FILE_SIZE / 1024 / 1024,
		}),
		deleteButtonLabel: t('ui__file_upload__delete_button'),
		unsupportedFormatErrorMessage: t(
			'ui__file_upload__unsupported_pdf_format_error',
		),
		sizeTooLargeErrorMessage: t('ui__file_upload__file_size_error', {
			maxSize: MAX_FILE_SIZE / 1024 / 1024,
		}),
	}

	useImperativeHandle(
		ref,
		() => ({
			setError: (error: string) => {
				fileUploadRef.current?.setError?.(error)
			},
			clearError: () => {
				fileUploadRef.current?.clearError?.()
			},
			validate: () => {
				return new Promise((resolve) => {
					const isValid = validatePdf({
						fileUploadRef,
						uploadErrorMessage,
						containerRef,
					})
					if (isValid) {
						fileUploadRef.current?.clearError?.()
					}
					resolve({ isValid })
				})
			},
		}),
		[uploadErrorMessage],
	)

	return (
		<div className="flex w-full flex-col" ref={containerRef}>
			<label className={fieldLabelCls}>
				{t('generator__content_form_section__pdf_screen__title')}
			</label>
			<FileUploadToMediaStorage
				uploadPurpose="pdf"
				ref={fileUploadRef}
				accept={ACCEPTED_MIME_TYPES}
				initialFileList={file ? [file] : []}
				maxFileSize={MAX_FILE_SIZE}
				translations={fileUploadTranslations}
				hideSingleFilePreview
				onUploadComplete={(
					uploaded: TUploadedMedia,
					original: File,
				) => {
					setPdf(uploaded)
					setFile(original)
					fileUploadRef.current?.clearError?.()
				}}
				onDelete={() => {
					setPdf(null)
					setFile(null)
				}}
			/>
		</div>
	)
})

PdfUpload.displayName = 'PdfUpload'
